import type { EmailAccountSummary, WorkspaceSummary } from "@spamjam/contracts";

type Tone = "good" | "warn" | "bad" | "neutral";

export interface DashboardProtectionState {
  active: boolean;
  billingRequired: boolean;
  connectedDetail: string;
  connectedTone: Tone;
  filterStatus: "Active" | "Inactive" | "Paused" | "Waiting";
  filterDetail: string;
  filterTone: Tone;
}

export function dashboardProtectionState(input: {
  accounts: Pick<EmailAccountSummary, "workspaceId" | "status">[];
  workspaces: Pick<WorkspaceSummary, "id" | "entitlement">[];
  killSwitch: boolean;
}): DashboardProtectionState {
  const entitlementByWorkspace = new Map(
    input.workspaces.map((workspace) => [workspace.id, workspace.entitlement]),
  );
  const billingRequired = input.accounts.some(
    (account) =>
      entitlementByWorkspace.get(account.workspaceId) === "payment_required",
  );
  if (billingRequired)
    return {
      active: false,
      billingRequired: true,
      connectedDetail: "Connected · filtering inactive",
      connectedTone: "bad",
      filterStatus: "Inactive",
      filterDetail: "Free trial ended · subscription required",
      filterTone: "bad",
    };

  if (input.killSwitch)
    return {
      active: false,
      billingRequired: false,
      connectedDetail: input.accounts.length
        ? "Connected · filtering paused"
        : "Connection needed",
      connectedTone: "warn",
      filterStatus: "Paused",
      filterDetail: "Global pause is on",
      filterTone: "warn",
    };

  const active = input.accounts.some(
    (account) =>
      account.status === "active" &&
      entitlementByWorkspace.has(account.workspaceId),
  );
  if (active)
    return {
      active: true,
      billingRequired: false,
      connectedDetail: "Real-time filtering enabled",
      connectedTone: "good",
      filterStatus: "Active",
      filterDetail: "Matches move to Gmail Spam",
      filterTone: "good",
    };

  return {
    active: false,
    billingRequired: false,
    connectedDetail: input.accounts.length
      ? "Mailbox action required"
      : "Connection needed",
    connectedTone: input.accounts.length ? "warn" : "neutral",
    filterStatus: "Waiting",
    filterDetail: input.accounts.length
      ? "Resume or reconnect Gmail to begin"
      : "Connect Gmail to begin",
    filterTone: input.accounts.length ? "warn" : "neutral",
  };
}
